import React, { useEffect, useMemo, useState } from "react";
import { Activity, Clock, MessageCircle, PackageCheck, Search, ShieldCheck, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ACTIVE_GAME_CATALOG, GAME_CATALOG } from "../data/gameCatalog";
import {
  FallbackImage,
  ProductInfoBadges,
  SeoMeta,
  SiteFooter,
  SiteNavbar,
  dataUrl,
  prefetchDataFile,
  preloadImage,
  statusLabel,
} from "../shared/site";
import { apiUrl } from "../shared/api";
import "../index.css";

const CATALOG_FILTERS = [
  { id: "all", label: "All games" },
  { id: "active", label: "Available" },
  { id: "new", label: "New" },
  { id: "coming", label: "Coming soon" },
];

const STATUS_PRIORITY = {
  undetected: 0,
  updating: 1,
  frozen: 2,
  detected: 3,
};

const PRODUCT_STATUS_BADGE_CLASSES = {
  undetected: "border-green-300/35 bg-green-500/15 text-green-100",
  updating: "border-yellow-300/35 bg-yellow-500/15 text-yellow-100",
  frozen: "border-slate-300/25 bg-slate-500/15 text-slate-100",
  detected: "border-red-300/35 bg-red-500/15 text-red-100",
};

const FALLBACK_FEATURED_PRODUCTS = [
  { dataFile: "lol.json", name: "LEGENDSENSE" },
  { dataFile: "pubg.json", name: "JARVIS" },
  { dataFile: "gta.json", name: "1337 RAGE MP" },
  { dataFile: "arenabreakout.json", name: "VATA" },
  { dataFile: "gta.json", name: "VANISH RAGE MP" },
  { dataFile: "dayz.json", name: "AUTHORITY" },
  { dataFile: "gta.json", name: "NIGHTFALL ALT V" },
  { dataFile: "armareforger.json", name: "MALEVOLENCE" },
];

const DISCORD_INVITE_URL = "https://discord.gg/HdZBtMFsWA";

const DEFAULT_DISCORD_STATS = {
  name: "HackSeller Discord",
  instantInvite: DISCORD_INVITE_URL,
  memberCount: null,
  presenceCount: null,
};

function formatDiscordCount(value) {
  if (!Number.isFinite(value)) return "-";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function useDiscordStats() {
  const [stats, setStats] = useState(DEFAULT_DISCORD_STATS);

  useEffect(() => {
    let active = true;

    fetch(apiUrl("/api/discord/stats"), { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data) return;

        setStats({
          name: data.name || DEFAULT_DISCORD_STATS.name,
          instantInvite: data.instantInvite || DEFAULT_DISCORD_STATS.instantInvite,
          memberCount: Number.isFinite(data.memberCount) ? data.memberCount : null,
          presenceCount: Number.isFinite(data.presenceCount) ? data.presenceCount : null,
        });
      })
      .catch(() => null);

    return () => {
      active = false;
    };
  }, []);

  return stats;
}

function HeroSection({ stats }) {
  return (
    <section className="cinematic-panel relative pt-28 flex flex-col lg:flex-row items-center justify-center px-6 md:px-12 py-16 bg-purple-950/20 backdrop-blur-[2px] rounded-b-3xl shadow-md gap-10 lg:gap-20 border-b border-purple-400/20 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6 }}
        className="text-center lg:text-left max-w-3xl"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 mb-5 rounded-md bg-purple-500/10 border border-purple-300/35 text-purple-100 text-base md:text-lg font-bold shadow-[0_0_22px_rgba(216,180,254,0.12)]">
          <Sparkles size={18} />
          Live catalog and product status
        </div>

        <h2 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight">
          Welcome on <span className="text-purple-500">hackseller.store</span>
        </h2>

        <p className="text-lg md:text-2xl text-gray-300 max-w-2xl leading-relaxed">
          Multifunctional hacks for popular online games.
Developed by professional developers, our cheats deliver the most advanced and secure solutions to dominate your favorite games.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-3 max-w-2xl mx-auto sm:grid-cols-3 lg:mx-0">
          <div className="holo-stat rounded-2xl bg-gray-950/50 border border-purple-400/20 p-4">
            <p className="text-3xl md:text-4xl font-extrabold text-white">{stats.active}</p>
            <p className="text-gray-300 text-base md:text-lg font-bold">Available games</p>
          </div>
          <div className="holo-stat rounded-2xl bg-gray-950/50 border border-purple-400/20 p-4">
            <p className="text-3xl md:text-4xl font-extrabold text-purple-300">{stats.newItems}</p>
            <p className="text-gray-300 text-base md:text-lg font-bold">New games</p>
          </div>
          <div className="holo-stat rounded-2xl bg-gray-950/50 border border-purple-400/20 p-4">
            <p className="text-3xl md:text-4xl font-extrabold text-gray-200">{stats.comingSoon}</p>
            <p className="text-gray-300 text-base md:text-lg font-bold">Upcoming games</p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-3 lg:justify-start">
          <Link
            to="/Status"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-300/25 bg-green-500/10 px-5 py-3 text-lg font-extrabold text-green-100 transition hover:bg-green-500/20"
          >
            <Activity size={20} />
            Live status
          </Link>
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-purple-300/30 bg-purple-600 px-5 py-3 text-lg font-extrabold text-white transition hover:bg-purple-500"
          >
            <MessageCircle size={20} />
            Discord support
          </a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="relative flex items-center justify-center shrink-0"
      >
        <div className="hero-showcase w-72 md:w-80 rounded-2xl border border-purple-400/25 bg-gray-950/45 shadow-2xl overflow-hidden">
          <div className="relative aspect-square flex items-center justify-center overflow-hidden">
            <video
  autoPlay
  loop
  muted
  playsInline
  preload="metadata"
  className="absolute inset-0 z-0 w-full h-full object-cover"
>
  <source src="/images/animation.mp4" type="video/mp4" />
</video>
            <div className="absolute inset-0 bg-black/45" />
          </div>
          <div className="grid grid-cols-2 divide-x divide-purple-400/20 border-t border-purple-400/20">
            <div className="p-4">
              <PackageCheck className="text-green-400 mb-2" size={22} />
              <p className="text-base md:text-lg text-gray-300 leading-tight">Safe products and fast delivery</p>
            </div>
            <div className="p-4">
              <Clock className="text-yellow-300 mb-2" size={22} />
              <p className="text-base md:text-lg text-gray-300 leading-tight">Status refresh in real time</p>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function SearchBar({ query, setQuery, resultCount, selectedFilter, setSelectedFilter }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="px-3 py-6 sm:px-6 md:p-10 flex justify-center"
    >
      <div className="w-full max-w-3xl">
        <div className="relative">
          <Search className="absolute left-4 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search a game..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="home-game-search w-full pl-12 pr-4 py-3 border border-purple-400/20 bg-gray-950/65 text-white rounded-lg shadow-md focus:outline-none focus:ring-4 focus:ring-purple-600 text-xl md:text-2xl"
          />
        </div>

        <div className="mt-4 flex flex-wrap justify-center gap-2 sm:gap-3">
          {CATALOG_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              onClick={() => setSelectedFilter(filter.id)}
              className={`px-3 py-2 sm:px-4 rounded-lg border text-base md:text-lg font-bold transition ${
                selectedFilter === filter.id
                  ? "bg-purple-600 border-purple-400 text-white"
                  : "bg-gray-950/55 border-purple-400/20 text-gray-200 hover:border-purple-500/70"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <p className="mt-3 text-center text-base md:text-lg text-gray-300">
          {resultCount} result{resultCount > 1 ? "s" : ""}
        </p>
      </div>
    </motion.div>
  );
}

function PopularProductsSection() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let active = true;

    Promise.all(
      [
        fetch(dataUrl("featured-products.json"), { cache: "no-store" })
          .then((response) => (response.ok ? response.json() : []))
          .catch(() => []),
        Promise.all(ACTIVE_GAME_CATALOG.map(async (game) => {
        try {
          const response = await fetch(dataUrl(game.dataFile), { cache: "no-store" });
          if (!response.ok) return [];

          const rows = await response.json();
          if (!Array.isArray(rows)) return [];

          return rows.map((item, index) => ({
            ...item,
            dataFile: game.dataFile,
            index,
            gameId: game.id,
            gameName: game.name,
            gameImage: game.image,
            gameIsNew: Boolean(game.isNew),
          }));
        } catch {
          return [];
        }
      })),
      ]
    ).then(([featuredProducts, groups]) => {
      if (!active) return;

      const allProducts = groups
        .flat()
        .filter((item) => item.name && item.link);
      const featuredList = Array.isArray(featuredProducts) && featuredProducts.length > 0
        ? featuredProducts
        : FALLBACK_FEATURED_PRODUCTS;
      const nextProducts = featuredList
        .map((featuredProduct) => allProducts.find((item) => {
          if (item.dataFile !== featuredProduct.dataFile) return false;
          if (Number.isInteger(featuredProduct.index)) return item.index === featuredProduct.index;
          return String(item.name || "").toLowerCase() === String(featuredProduct.name || "").toLowerCase();
        }))
        .filter(Boolean)
        .slice(0, 8);

      if (nextProducts.length === 0) {
        setProducts(
          allProducts
            .sort((a, b) => {
              const statusDelta = (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9);
              if (statusDelta !== 0) return statusDelta;
              if (a.gameIsNew !== b.gameIsNew) return a.gameIsNew ? -1 : 1;
              return String(a.name).localeCompare(String(b.name));
            })
            .slice(0, 8)
        );
        return;
      }

      setProducts(nextProducts);
    });

    return () => {
      active = false;
    };
  }, []);

  if (products.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55 }}
      className="max-w-6xl mx-auto px-3 sm:px-6 md:px-10 pb-10"
    >
      <div className="mb-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-purple-400/20" />
        <span className="rounded-md border border-purple-400/20 bg-gray-950/65 px-3 py-1 text-base font-extrabold uppercase text-purple-200">
          Popular product
        </span>
        <span className="h-px flex-1 bg-purple-400/20" />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:gap-4 xl:grid-cols-4">
        {products.map((product) => {
          const statusClass = PRODUCT_STATUS_BADGE_CLASSES[product.status] || "border-purple-300/30 bg-purple-500/15 text-purple-100";

          return (
          <Link
            key={`${product.gameName}-${product.link}-${product.name}`}
            to={product.link}
            onMouseEnter={() => preloadImage(product.logo || product.gameImage)}
            onFocus={() => preloadImage(product.logo || product.gameImage)}
            className="group overflow-hidden rounded-lg border border-purple-400/20 bg-gray-950/60 shadow-[0_16px_45px_rgba(124,58,237,0.14)] transition hover:border-purple-300/50 hover:bg-purple-950/35"
          >
            <div className="relative aspect-video overflow-hidden bg-black">
              <FallbackImage
                src={product.logo || product.gameImage}
                alt={product.name}
                label={product.name}
                className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.03]"
              />
              <span className={`status-pulse status-${product.status} absolute left-3 top-3 rounded-md border px-2.5 py-1 text-xs font-extrabold uppercase ${statusClass}`}>
                {statusLabel(product.status)}
              </span>
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-xl font-extrabold text-white group-hover:text-purple-100">{product.name}</p>
                  <p className="truncate text-base font-bold text-gray-400">{product.gameName}</p>
                </div>
                {product.price && (
                  <span className="shrink-0 rounded-md border border-purple-300/25 bg-purple-500/10 px-2 py-1 text-lg font-extrabold leading-none text-white">
                    {product.price}
                  </span>
                )}
              </div>

              <div className="mt-3">
              <ProductInfoBadges item={product} compact />
              </div>
            </div>
          </Link>
          );
        })}
      </div>
    </motion.section>
  );
}

function SupportSection() {
  const discordStats = useDiscordStats();
  const discordName = discordStats.name || DEFAULT_DISCORD_STATS.name;
  const discordInvite = discordStats.instantInvite || DISCORD_INVITE_URL;

  return (
    <motion.section
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55 }}
      className="max-w-6xl mx-auto px-3 sm:px-6 md:px-10 pb-12"
    >
      <div className="mb-10 flex items-center gap-4">
        <span className="h-px flex-1 bg-purple-400/20" />
        <span className="rounded-md border border-purple-400/20 bg-gray-950/65 px-3 py-1 text-base font-extrabold uppercase text-purple-200">
          Support
        </span>
        <span className="h-px flex-1 bg-purple-400/20" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="home-discord-preview overflow-hidden rounded-lg border border-purple-400/20 bg-gray-950/65 shadow-[0_16px_45px_rgba(124,58,237,0.14)]">
          <div className="flex items-center justify-between border-b border-purple-400/15 px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-purple-300/25 bg-purple-500/15 text-lg font-extrabold text-purple-100">
                PS
              </div>
              <div>
                <p className="text-lg font-extrabold text-white">{discordName}</p>
                <p className="text-sm font-bold text-purple-200/80">community / support access</p>
              </div>
            </div>
            <span className="rounded-md border border-green-400/20 bg-green-500/10 px-2 py-1 text-sm font-bold text-green-200">
              Online
            </span>
          </div>

          <div className="home-discord-fallback px-5 py-8 md:px-8 md:py-10">
            <div className="mx-auto max-w-xl rounded-lg border border-purple-400/20 bg-black/30 p-5 text-center shadow-[0_18px_48px_rgba(2,6,23,0.35)]">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl border border-purple-300/25 bg-purple-500/15 text-2xl font-extrabold text-purple-100">
                HS
              </div>
              <p className="text-3xl font-extrabold text-white">{discordName}</p>
              <p className="mt-2 text-lg leading-relaxed text-gray-300">
                Join the Discord to open tickets, check support availability and follow product updates.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-purple-400/15 bg-purple-500/10 p-3">
                  <p className="text-2xl font-extrabold text-white">{formatDiscordCount(discordStats.memberCount)}</p>
                  <p className="text-sm font-bold uppercase text-purple-200/80">members</p>
                </div>
                <div className="rounded-lg border border-green-400/15 bg-green-500/10 p-3">
                  <p className="text-2xl font-extrabold text-white">{formatDiscordCount(discordStats.presenceCount)}</p>
                  <p className="text-sm font-bold uppercase text-green-200/80">online</p>
                </div>
              </div>
              <a
                href={discordInvite}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg border border-purple-300/30 bg-purple-600 px-5 py-3 text-lg font-extrabold text-white transition hover:bg-purple-500"
              >
                <MessageCircle size={20} />
                Join Discord
              </a>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
          <a
            href={discordInvite}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border border-purple-400/20 bg-purple-600/15 p-4 text-purple-100 transition hover:bg-purple-600/25"
          >
            <MessageCircle className="mb-3 text-purple-200" size={24} />
            <p className="text-xl font-extrabold">Discord</p>
            <p className="text-base text-purple-100/80">Open support</p>
          </a>
          <div className="rounded-lg border border-yellow-400/20 bg-yellow-500/10 p-4 text-yellow-100">
            <Clock className="mb-3 text-yellow-200" size={24} />
            <p className="text-xl font-extrabold">09:00-23:00</p>
            <p className="text-base text-yellow-100/80">Lisbon time</p>
          </div>
          <Link to="/faq" className="rounded-lg border border-green-400/20 bg-green-500/10 p-4 text-green-100 transition hover:bg-green-500/15">
            <ShieldCheck className="mb-3 text-green-200" size={24} />
            <p className="text-xl font-extrabold">FAQ</p>
            <p className="text-base text-green-100/80">Before buying</p>
          </Link>
        </div>
      </div>
    </motion.section>
  );
}

function GameCard({ game }) {
  const preloadGame = () => {
    prefetchDataFile(game.dataFile);
    preloadImage(game.image);
  };

  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className={`game-card-animated relative bg-gray-950/45 backdrop-blur-md rounded-lg shadow-lg overflow-hidden border w-full aspect-square transition ${
        game.comingSoon
          ? "border-purple-400/15 opacity-80"
          : "border-purple-400/20 hover:shadow-purple-500/40 hover:shadow-xl"
      }`}
      onMouseEnter={preloadGame}
      onFocus={preloadGame}
    >
      {game.isNew && (
        <div className="arcade-badge absolute top-2 left-2 z-10 text-sm md:text-base font-extrabold px-3 py-1 uppercase">
          New
        </div>
      )}

      {game.comingSoon && (
        <div className="arcade-badge arcade-badge-muted absolute top-2 left-2 z-10 text-xs md:text-sm font-bold px-3 py-1 uppercase">
          Coming soon
        </div>
      )}

      <FallbackImage
        src={game.image}
        alt={game.name}
        label={game.name}
        loading="lazy"
        className={`game-card-image object-cover w-full h-full ${game.comingSoon ? "grayscale-[0.35]" : ""}`}
      />

      <div className="game-card-title absolute bottom-0 left-0 w-full bg-black/70 text-white text-sm sm:text-base md:text-lg font-bold text-center py-1 px-2">
        {game.name}
      </div>
    </motion.div>
  );

  if (game.comingSoon) {
    return <div className="group">{cardContent}</div>;
  }

  return (
    <Link to={game.canonicalPath || `/games/${game.id}`} className="group">
      {cardContent}
    </Link>
  );
}

function GameGrid({ games }) {
  if (games.length === 0) {
    return (
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="rounded-lg border border-purple-400/20 bg-gray-950/45 backdrop-blur-md p-10 text-center text-gray-300 text-2xl">
          No games found for your search.
        </div>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-3 min-[430px]:grid-cols-4 md:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 justify-center max-w-6xl mx-auto p-3 sm:p-6 md:p-10">
      {games.map((game) => (
        <GameCard key={game.id} game={game} />
      ))}
    </section>
  );
}

function AboutSection() {
  const reasons = [
    {
      title: "Wide selection",
      text: "a broad catalog across FPS, survival, extraction, MMO, and competitive titles.",
    },
    {
      title: "Setup support",
      text: "clear help for preparation, compatibility checks, delivery, and common launch issues.",
    },
    {
      title: "Frequent maintenance",
      text: "product status and catalog details are reviewed as games and anti-cheat updates change.",
    },
    {
      title: "Fast access",
      text: "simple purchase flow, visible product status, and quick access to instructions after payment.",
    },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8 }}
      className="cinematic-panel px-6 md:px-12 py-16 mb-20 text-gray-200 max-w-5xl mx-auto bg-gray-950/45 backdrop-blur-lg rounded-lg shadow-lg border border-purple-400/20 overflow-hidden"
    >
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <div>
          <p className="text-purple-300 text-lg font-extrabold uppercase">About private cheats</p>
          <h2 className="mt-2 text-4xl md:text-5xl font-bold text-white leading-tight">
            Pick tools with clearer status and less guesswork.
          </h2>
          <p className="mt-5 text-xl md:text-2xl leading-relaxed text-gray-300">
            <span className="font-semibold text-purple-300">HackSeller</span> brings the catalog,
            compatibility notes, live status, and support path into one place so you can check a
            product before committing to it.
          </p>
        </div>

        <div>
          <h3 className="text-3xl md:text-4xl font-semibold text-white">Why choose us?</h3>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reasons.map((reason) => (
              <div key={reason.title} className="rounded-lg border border-purple-400/15 bg-black/25 p-4">
                <p className="text-xl font-extrabold text-white">{reason.title}</p>
                <p className="mt-2 text-lg leading-relaxed text-gray-300">{reason.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

export default function App() {
  const [query, setQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");

  const stats = useMemo(() => {
    return GAME_CATALOG.reduce(
      (totals, game) => ({
        active: totals.active + (game.dataFile ? 1 : 0),
        comingSoon: totals.comingSoon + (game.comingSoon ? 1 : 0),
        newItems: totals.newItems + (game.isNew ? 1 : 0),
      }),
      { active: 0, comingSoon: 0, newItems: 0 }
    );
  }, []);

  const visibleGames = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return [...GAME_CATALOG]
      .filter((game) => game.name.toLowerCase().includes(normalizedQuery))
      .filter((game) => {
        if (selectedFilter === "active") return Boolean(game.dataFile);
        if (selectedFilter === "new") return Boolean(game.isNew);
        if (selectedFilter === "coming") return Boolean(game.comingSoon);
        return true;
      })
      .sort((a, b) => {
        const aPriority = a.isNew ? 1 : 0;
        const bPriority = b.isNew ? 1 : 0;
        return bPriority - aPriority;
      });
  }, [query, selectedFilter]);

  return (
    <div className="relative min-h-screen flex flex-col bg-transparent text-white font-jersey overflow-hidden">
      <SeoMeta
        title="Live catalog"
        description="Browse HackSeller games, product categories, availability and live product status."
        canonicalPath="/"
      />
      <div className="relative z-10">
        <SiteNavbar />
        <HeroSection stats={stats} />
        <SearchBar
          query={query}
          setQuery={setQuery}
          resultCount={visibleGames.length}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
        <GameGrid games={visibleGames} />
        <PopularProductsSection />
        <SupportSection />
        <AboutSection />
        <SiteFooter />
      </div>
    </div>
  );
}
